import { FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Card, CardBody, Col, Container, Row } from 'reactstrap';
import { Organization, ReduxState } from 'types';
import { useConfirmApi } from 'utils/api/ApiHooks';
import Page from 'views/Layout/Pages/Page';
import EmptyState from 'views/Widgets/EmptyState';
import ValidatedForm from 'views/Widgets/Forms/ValidatedForm';
import RichTextViewer from 'views/Widgets/Inputs/RichTextViewer';
import { INPUT_TYPES } from 'views/Widgets/Inputs/ValidatedInputTypes';
import Loading from 'views/Widgets/Loading';
import * as consts from '../../consts/consts';

interface SurveyQuestionDefinition {
  type: string;
  label: string;
  helperText?: string;
  options?: Array<{ id: string; name: string }>;
  objects?: Array<{ name: string; value: number; helperText?: string }>;
}

interface SurveyQuestion {
  id: number;
  order: number;
  required: boolean;
  question: SurveyQuestionDefinition;
  answer: { id: number; answer: Record<string, unknown> } | null;
}

interface ParticipationData {
  id: number;
  survey_instance: number;
  survey_name: string;
  survey_type: string;
  questions: SurveyQuestion[];
  has_answers: boolean;
  is_expired: boolean;
}

const getInitialValue = (
  type: string,
  answerJson: Record<string, unknown> | undefined
): unknown => {
  if (!answerJson) return undefined;
  switch (type.toUpperCase()) {
    case INPUT_TYPES.STAR_RATING:
      return answerJson.rating;
    case INPUT_TYPES.TEXTAREA:
    case INPUT_TYPES.RICH_TEXT_EDITOR:
      return answerJson.text;
    case INPUT_TYPES.SWITCH:
      return answerJson.value;
    case INPUT_TYPES.LIKERT:
      return answerJson.value;
    case INPUT_TYPES.MULTIPLE_CHOICE:
    case INPUT_TYPES.DROPDOWN:
      return answerJson.value;
    case INPUT_TYPES.DATE_PICKER:
      return answerJson.date;
    default:
      return answerJson.value;
  }
};

const getAnswerJson = (
  type: string,
  value: unknown
): Record<string, unknown> => {
  switch (type.toUpperCase()) {
    case INPUT_TYPES.STAR_RATING:
      return { rating: value };
    case INPUT_TYPES.TEXTAREA:
    case INPUT_TYPES.RICH_TEXT_EDITOR:
      return { text: value };
    case INPUT_TYPES.SWITCH:
      return { value: !!value };
    case INPUT_TYPES.LIKERT:
      // value is a scalar for a single-statement question, or a dict keyed by
      // statement name (see LikertScaleInput's hasMultipleValues) for a
      // multi-statement one — store it as-is either way.
      return { value };
    case INPUT_TYPES.MULTIPLE_CHOICE:
    case INPUT_TYPES.DROPDOWN:
      return { value };
    case INPUT_TYPES.DATE_PICKER:
      return { date: value };
    default:
      return { value };
  }
};

const CustomSurveyResponsePage: FC = () => {
  const { formatMessage } = useIntl();
  const history = useHistory();
  const { participationId } = useParams<{ participationId: string }>();
  const currentOrganization = useSelector<ReduxState, Organization | undefined>(
    (state) => state.currentOrganization
  );

  const { status, data: participation } = useConfirmApi<ParticipationData>({
    method: 'GET',
    url: `custom-survey/participations/${participationId}`,
  });

  const initialObject = useMemo(() => {
    if (!participation) return { id: Number(participationId) };
    const answerFields: Record<string, unknown> = { id: participation.id };
    for (const q of participation.questions) {
      const fieldName = `answer_${q.id}`;
      answerFields[fieldName] = getInitialValue(
        q.question.type,
        q.answer?.answer
      );
    }
    return answerFields;
  }, [participation, participationId]);

  const inputs = useMemo(() => {
    if (!participation) return [];
    return participation.questions.map((q) => ({
      name: `answer_${q.id}`,
      label: <RichTextViewer model={q.question.label} />,
      helperText: <RichTextViewer model={q.question.helperText} />,
      type: q.question.type,
      required: q.required,
      options: q.question.options,
      objects: q.question.objects,
    }));
  }, [participation]);

  const transformObjectBeforeSubmit = useCallback(
    (formObj: Record<string, unknown>) => {
      if (!participation) return formObj;
      const answers = participation.questions
        .map((q) => {
          const val = formObj[`answer_${q.id}`];
          if (val === undefined || val === null) return null;
          return {
            question_id: q.id,
            answer: getAnswerJson(q.question.type, val),
          };
        })
        .filter(Boolean);
      return {
        id: participation.id,
        organization: currentOrganization?.id,
        answers,
      };
    },
    [participation, currentOrganization]
  );

  const onSuccess = useCallback(() => {
    // send the user back to their "Surveys & performance" tab with this survey
    // selected (/profile/performance resolves to the current user's profile)
    history.push(
      `${consts.MY_PERFORMANCE(formatMessage).path}?survey_id=${participationId}`
    );
  }, [history, formatMessage, participationId]);

  if (status === 'LOADING' || status === 'DISABLED') {
    return <Loading />;
  }

  if (!participation || status === 'ERROR') {
    return (
      <EmptyState
        title={formatMessage({
          id: 'app.views.custom_surveys.response_page.not_found',
          defaultMessage: 'Survey not found.',
        })}
      />
    );
  }

  if (participation.is_expired) {
    return (
      <Container className="mt-5">
        <EmptyState
          title={formatMessage({
            id: 'app.views.custom_surveys.response_page.expired',
            defaultMessage: 'This survey is no longer accepting responses.',
          })}
        />
      </Container>
    );
  }

  return (
    <Container>
      <Row className="justify-content-center">
        <Col xl={10} lg={10}>
          <Page title={participation.survey_name}>
            <Card>
              <CardBody>
                <ValidatedForm
                  method="PATCH"
                  url="custom-survey/participations"
                  object={initialObject}
                  inputs={inputs}
                  transformObjectBeforeSubmit={transformObjectBeforeSubmit}
                  callback={onSuccess}
                  submitText={formatMessage({
                    id: 'app.views.custom_surveys.response_page.save_answers',
                    defaultMessage: 'Save answers',
                  })}
                  buttonIsBlock={false}
                />
              </CardBody>
            </Card>
          </Page>
        </Col>
      </Row>
    </Container>
  );
};

export default CustomSurveyResponsePage;
